import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { doFinalCheckIn } from "../../redux/final-checkln-process";
import {
  addNights,
  formatDate,
  formatUSAmount,
} from "../../utils/helperFunctions/helperFunctions";
import { Link, useNavigate } from "react-router-dom";

const BookingDetails = ({ bookingDetails }) => {
  const { boarding_pass_link, luggage_tag_links, booking_confirmation_pdf } =
    useSelector((state) => state.finalChecklnProcess);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const today = new Date();
  const sailingDate = new Date(bookingDetails.sailing_date);
  const diffTime = sailingDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  useEffect(() => {
    const payload = { booking_id: bookingDetails.booking_id };
    dispatch(doFinalCheckIn({ payload }));
  }, [bookingDetails, dispatch]);

  const download_docs = useMemo(() => {
    const docs = [
      { title: "Boarding Pass", url: "" },
      { title: "Luggage Tags", url: "" },
      { title: "Booking Confirmation", url: booking_confirmation_pdf },
    ];

    if (diffDays < 7) {
      docs[1].url = luggage_tag_links;
    }

    if (diffDays < 7) {
      docs[0].url = boarding_pass_link;
    }

    return docs;
  }, [
    boarding_pass_link,
    luggage_tag_links,
    booking_confirmation_pdf,
    diffDays,
  ]);

  return (
    bookingDetails && (
      <div className="p-4 md:p-8">
        <h1 className="text-3xl text-mv-darkblue text-center md:text-left font-bold">
          MANAGE MY BOOKING
        </h1>
        {diffDays < 21 && bookingDetails.cruise_id === 77 && (
          <div className="bg-blue-200 rounded-md p-4 mt-4 flex justify-between flex-col md:flex-row gap-4 items-center">
            <div className="flex gap-4 w-full md:w-auto justify-start md:justify-normal items-center">
              <div>
                <img
                  src={`${process.env.PUBLIC_URL}/assets/manageBookings/clock.svg`}
                  alt="timer-bg"
                  className="w-16"
                />
              </div>
              <div>
                <p className="text-mv-darkblue text-lg font-semibold">
                  Get ready to sail!
                </p>
                <p className="text-mv-darkblue text-lg font-semibold">
                  ARRIVAL TIME - {bookingDetails.check_in_time}
                </p>
                <p>
                  REMINDER! <br /> To speed up check-in and reduce wait times,
                  all guests should arrive together within the given appointment
                  window.
                </p>
              </div>
            </div>
          </div>
        )}
        <div className="bg-white shadow-md rounded-md mt-8 p-6">
          <h2 className="text-2xl text-mv-darkblue font-semibold">
            {bookingDetails.itinerary_title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-4">
            <div className="flex justify-between">
              <p className="font-semibold text-mv-darkblue">BOOKING DATE</p>
              <p>{bookingDetails.booking_date}</p>
            </div>
            <div className="flex justify-between">
              <p className="font-semibold text-mv-darkblue">SHIP</p>
              <p>{bookingDetails.cruise_name}</p>
            </div>
            <div className="flex justify-between">
              <p className="font-semibold text-mv-darkblue">BOOKING ID</p>
              <p>{bookingDetails.booking_id}</p>
            </div>
            <div className="flex justify-between">
              <p className="font-semibold text-mv-darkblue">ROUTE</p>
              <p>{bookingDetails.itinerary_details[0]?.activity}</p>
            </div>
            {bookingDetails.payment_details.due_amount > 0 && (
              <div className="flex justify-between">
                <p className="font-semibold text-mv-darkblue">
                  BALANCE REMAINING
                </p>
                <p>
                  ${formatUSAmount(bookingDetails.payment_details.due_amount)}
                </p>
              </div>
            )}
            <div className="flex justify-between">
              <p className="font-semibold text-mv-darkblue">SAILING DATE</p>
              <p>{formatDate(bookingDetails.sailing_date)}</p>
            </div>
            {bookingDetails.payment_details.due_amount > 0 && (
              <div className="flex justify-between">
                <p className="font-semibold text-mv-darkblue">DUE DATE</p>
                <p>{bookingDetails.payment_details?.due_date}</p>
              </div>
            )}
            <div className="flex justify-between">
              <p className="font-semibold text-mv-darkblue">RETURN DATE</p>
              <p>
                {formatDate(
                  addNights(
                    bookingDetails.sailing_date,
                    +bookingDetails?.itinerary_title[0]
                  )
                )}
              </p>
            </div>
            <div className="flex justify-between">
              <p className="font-semibold text-mv-darkblue">PORT DEPARTURE</p>
              <div className="flex flex-col text-right">
                <p>
                  {bookingDetails.cruise_id === 77
                    ? "Port Tampa Bay,"
                    : " Port of Palm Beach,"}
                </p>
                <Link
                  target="_blank"
                  to={
                    bookingDetails.cruise_id === 77
                      ? "https://maps.app.goo.gl/APc4p2iwK1seMXXK6"
                      : "https://maps.app.goo.gl/EHcTUqFGWsucGfwc9"
                  }
                  className="text-mv-darkblue underline"
                >
                  {bookingDetails.cruise_id === 77
                    ? "1331 McKay Street, Tampa, FL 33602(77) "
                    : "1 East 11th Street, Riviera Beach, FL 33404(61)"}
                </Link>
              </div>
            </div>
            <div className="flex gap-2 justify-between">
              <p className="font-semibold text-mv-darkblue">
                STATEROOM CATEGORY
              </p>
              <p>
                {[
                  ...new Set(
                    bookingDetails.guest_details.map((g) => g?.category_name)
                  ),
                ].join(" , ")}
              </p>
            </div>
            <div className="flex justify-between">
              <p className="font-semibold text-mv-darkblue">STATEROOM NUMBER</p>
              <p>
                {bookingDetails.room_with_guest_count
                  .map((g) => g?.room_no)
                  .join(",")}
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-5 md:flex-row justify-between mt-4">
            <div className="flex flex-col gap-5 md:flex-row justify-center">
              {download_docs.map((doc, index) => {
                if (doc.url) {
                  return (
                    <div
                      key={index}
                      className="flex gap-5 md:flex-row justify-center"
                    >
                      <button
                        onClick={() => {
                          if (
                            doc.title === "Luggage Tags" &&
                            Array.isArray(doc.url)
                          ) {
                            doc.url.forEach((link) => {
                              window.open(
                                link,
                                "_blank",
                                "noopener,noreferrer"
                              );
                            });
                          } else if (
                            doc.title === "Boarding Pass" &&
                            Array.isArray(doc.url)
                          ) {
                            doc.url.forEach((link) => {
                              window.open(
                                link,
                                "_blank",
                                "noopener,noreferrer"
                              );
                            });
                          } else {
                            window.open(
                              doc.url,
                              "_blank",
                              "noopener,noreferrer"
                            );
                          }
                        }}
                        className="bg-mv-darkblue flex items-center gap-2 text-white font-semibold px-4 py-1 rounded"
                      >
                        <div className="flex justify-center items-center">
                          <img
                            className="w-8 h-8"
                            src={`${process.env.PUBLIC_URL}/assets/manageBookings/download-icon.svg`}
                            alt="download"
                          />
                        </div>
                        <div>{doc.title}</div>
                      </button>
                    </div>
                  );
                } else return null;
              })}
            </div>
            <div className="flex flex-col gap-5 md:flex-row justify-center">
              <button
                onClick={() => window.open("tel:1800-123-4567", "_blank")}
                className="bg-gray-300 text-black px-4 py-1 rounded"
              >
                CALL SUPPORT
              </button>
              {+bookingDetails.payment_details.due_amount > 0 && (
                <button
                  onClick={() => navigate("?setShowPaymentModal=true")}
                  className="bg-mv-darkblue text-white px-4 py-1 rounded"
                >
                  MAKE A PAYMENT
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  );
};

export default BookingDetails;
